import PropTypes from 'prop-types';
import { cn } from '../../utils/cn';

/**
 * Button component with multiple variants
 * @param {object} props - Component props
 * @param {string} props.variant - Button style variant: "primary" | "cta" | "secondary" | "outline"
 * @param {string} props.size - Button size: "sm" | "md" | "lg"
 * @param {boolean} props.fullWidth - Whether button should take full width
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {string} props.type - Button type: "button" | "submit" | "reset"
 * @param {function} props.onClick - Click handler
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  type = 'button',
  onClick,
  children,
  className,
  ...props
}) => {
  const baseStyles =
    'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    primary:
      'bg-primary text-white hover:bg-primary-dark focus:ring-primary active:bg-primary-dark',
    cta: 'bg-cta text-white hover:bg-cta-dark focus:ring-cta active:bg-cta-dark',
    secondary:
      'bg-bg-light text-text-primary hover:bg-gray-200 focus:ring-accent active:bg-gray-200',
    outline:
      'border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-white focus:ring-primary active:bg-primary-dark',
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm rounded-md',
    md: 'px-6 py-2.5 text-base rounded-lg',
    lg: 'px-8 py-3.5 text-lg rounded-lg',
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'cta', 'secondary', 'outline']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default Button;























